import type { ApiRentalDealList } from 'src/types/rental';

import { useQuery } from '@tanstack/react-query';

import axios from 'src/utils/axios';

import { QueryKeys } from './QueryKeys';
import { LIVE_STALE_TIME } from './staleness';
import { useRequestSession } from './useRequestSession';

/**
 * Current renter's rental deals (with on-chain state). The 2a list endpoint
 * returns the full set `{ items, total }` without pagination, so a plain
 * useQuery is enough.
 *
 * Read under the account session (app#238): the api scopes it to the account's primary wallet,
 * and the api client sends the session's token with the request.
 *
 * Keyed by that session (app#248): the list is shown from the cache while it is asked again, and
 * a key shared by everybody showed the next person on a shared device the previous one's rentals —
 * for good, when the next person's own answer was a refusal.
 */
export function useListRentals(options?: { enabled?: boolean }) {
  const session = useRequestSession();

  return useQuery({
    queryKey: QueryKeys.forSession(QueryKeys.rentalDeals(), session),
    queryFn: async (): Promise<ApiRentalDealList> => {
      const response = await axios.get<ApiRentalDealList>('/buyer/rental-deals');
      return response.data;
    },
    staleTime: LIVE_STALE_TIME,
    enabled: options?.enabled ?? true,
  });
}
